import * as React from 'react';
import { RouteComponentProps } from 'react-router';
import { Link } from 'react-router-dom';
import { Control, Errors } from 'react-redux-form';
import { RouteDefinitions, getRoute } from '@common/route-definitions';
import { OptionsViewModel } from '@models/common.models';
import {
  getDefaultErrorMessages,
  hasValue,
  isValidCity,
  isValidZip,
  hasAlpha,
  hasNumber,
  isValidEmail
} from '@common/validators';
import { Select, CheckboxInline, Options, DateTimePicker, SpinnerPage } from '@common/components';
import { DynamicSelectIdentifier } from '@common/components/dynamic-select/dynamic-select-types';
import * as classes from './contract-cancellation.scss';
import * as moment from 'moment-timezone';
import { dateParser } from '@common/dateParser';
import {
  ContractCancellationModel,
  ContractCancellationNotificationModel,
  ContractPaymentInfoModel,
  ContractOrderCancellationModel
} from '@models/ContractCancellation';
import { PaymentType } from '@models/Payment';
import { Chip } from '@common/components';
import { FormWrapper } from '../../../common/components/form-wrapper/form-wrapper.component';
import { ContractDetailsViewModel } from '@models/Contract';

export interface ContractCancellationProperties extends RouteComponentProps<{ id: string }> {
  readonly refund: ContractCancellationModel | null;
  readonly paymentInfo: ContractPaymentInfoModel | null;
  readonly cancellationNotifications: Array<ContractCancellationNotificationModel>;
  readonly cancellationReasons: Array<OptionsViewModel>;
  readonly emailNotifications: Array<string>;
  readonly addressOverride: boolean;
  readonly refundByCheck: boolean;
  readonly hasSubmitted: boolean;
  readonly totalPrice: number;
  readonly termStart: Date;
  readonly termEnd: Date;
  readonly isTermination: boolean;
  readonly getContractDetails: (id: number) => void;
  readonly addEmail: (email: string) => void;
  readonly removeEmail: (email: string) => void;
  readonly getCancellationNotifications: (id: number) => void;
  readonly getCancellationReasons: (id: number) => void;
  readonly getPaymentInfo: (id: number) => void;
  readonly calculateCancellationRefund: (id: number, calcParams: Partial<ContractCancellationModel>) => void;
  readonly updateRefundAmount: (calcParams: Partial<ContractCancellationModel>) => void;
  readonly setOverride: (val: boolean) => void;
  readonly setRefundByCheck: (val: boolean) => void;
  readonly cancelContract: (
    id: number,
    formValue: ContractCancellationModel
  ) => Promise<ContractDetailsViewModel | undefined>;
  readonly cancelContractCommentStamp: (id: number, values: any, isCancel: boolean) => void;
  readonly terminateContract: (id: number, formValue: ContractOrderCancellationModel) => Promise<void>;
  readonly resetForm: () => void;
}

interface ContractCancellationState {
  invalidEmail: boolean;
}

export class ContractCancellationComponent extends React.Component<
  ContractCancellationProperties,
  ContractCancellationState
> {
  private contractId: number;
  private cancellationDate: Date;
  private adminFee: number;
  private serviceCharges: number;
  private pastDueServiceFee: number;

  constructor(props: ContractCancellationProperties) {
    super(props);

    this.state = {
      invalidEmail: false
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleRefundByCheck = this.handleRefundByCheck.bind(this);
    this.handleOverrideAddress = this.handleOverrideAddress.bind(this);
    this.isOverrideRequired = this.isOverrideRequired.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
  }

  componentDidMount() {
    this.props.resetForm();
    this.contractId = +this.props.match.params.id;
    this.cancellationDate = dateParser.now().toDate();
    this.props.getCancellationReasons(this.contractId);
    this.props.getCancellationNotifications(this.contractId);
    this.props.getPaymentInfo(this.contractId);
    this.props.calculateCancellationRefund(this.contractId, {
      cancellationDate: this.cancellationDate
    });
    if (this.props.match && this.props.match.params.id) {
      this.props.getContractDetails(+this.props.match.params.id);
    }
    // NOTE: Contract Cancellation Form is initialized in the Forms.ts file in _constants
  }

  componentWillUnmount() {
    this.props.setOverride(false);
  }

  handleAddNotification(e: Event) {
    e.preventDefault();
    e.stopPropagation();

    const input = document.getElementById('emailAddress') as HTMLInputElement;
    const email: string = input.value;
    if (email.trim().length > 0) {
      if (isValidEmail(email)) {
        this.props.addEmail(email);
        input.value = '';
      } else {
        this.setState(() => {
          return { invalidEmail: true };
        });
      }
    } else {
      this.setState(() => {
        return { invalidEmail: false };
      });
    }
  }

  handleRemoveNotification = (email: string) => {
    this.props.removeEmail(email);
  };

  handleChange = (form: ContractCancellationModel) => {
    if (
      form.cancellationDate &&
      dateParser.toDateString(this.cancellationDate) !== dateParser.toDateString(form.cancellationDate)
    ) {
      this.props.calculateCancellationRefund(+this.props.match.params.id, form);
      this.cancellationDate = form.cancellationDate;
    }
    let calcParams = {
      ...this.props.refund,
      adminFee: +form.adminFee,
      serviceCharges: +form.serviceCharges,
      pastDueServiceFee: +form.pastDueServiceFee
    };
    if (form.adminFee && this.adminFee !== calcParams.adminFee) {
      this.props.updateRefundAmount(calcParams);
      this.adminFee = calcParams.adminFee;
    }
    if (form.serviceCharges && this.serviceCharges !== calcParams.serviceCharges) {
      this.props.updateRefundAmount(calcParams);
      this.serviceCharges = calcParams.serviceCharges;
    }
    if (form.pastDueServiceFee && this.pastDueServiceFee !== calcParams.pastDueServiceFee) {
      this.props.updateRefundAmount(calcParams);
      this.pastDueServiceFee = calcParams.pastDueServiceFee;
    }
  };

  handleOverrideAddress = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.props.setOverride(event.target.checked);
  };

  handleRefundByCheck(event: React.ChangeEvent<HTMLInputElement>) {
    this.props.setRefundByCheck(event.target.checked);
  }

  handleSubmit(formValue: ContractCancellationModel) {
    if (this.props.isTermination) {
      var terminationForm = {
        contractId: this.contractId,
        cancellationDate: formValue.cancellationDate,
        reasonId: formValue.reason.id,
        reasonNote: formValue.reasonNote
      } as ContractOrderCancellationModel;
      this.handleTerminationSubmit(terminationForm);
    } else {
      this.handleCancellationSubmit(formValue);
    }
  }

  handleTerminationSubmit(formValue: ContractOrderCancellationModel) {
    this.props
      .terminateContract(this.contractId, formValue)
      .then(() => this.props.history.push(`/contract/${this.contractId}/details`));

    const cancellationDate = `Contract cancelled on ${moment(dateParser.toDateString(this.cancellationDate)).format(
      'MM/DD/YYYY'
    )}.`;
    const reason = this.props.cancellationReasons.find(reasons => reasons.id === formValue.reasonId);
    const reasonNote = formValue.reasonNote;
    const commentBody = [cancellationDate, reason, reasonNote].join('\n\n');
    this.props.cancelContractCommentStamp(this.contractId, { comment: commentBody }, true);
  }

  handleCancellationSubmit(formValue: ContractCancellationModel) {
    this.props
      .cancelContract(this.contractId, formValue)
      .then(() => this.props.history.push(`/contract/${this.contractId}/details`));

    if (this.props.refund) {
      const cancellationDate = `Contract cancelled on ${moment(dateParser.toDateString(this.cancellationDate)).format(
        'MM/DD/YYYY'
      )}.`;
      const amountPaid = `Amount Paid: $${((this.props.refund && this.props.refund.amountPaid) || 0).toFixed(2)}`;
      const totalPrice = `Total Price: $${((this.props.refund && this.props.refund.totalPrice) || 0).toFixed(2)}`;
      const avgRate = `Avg. Rate of Contract per ${this.props.refund.unitName}: $${(
        this.props.refund.averageUnitRate || 0
      ).toFixed(2)}`;
      const timeUsed = `${this.props.refund.unitName} Used: ${(this.props.refund.unitsUsed || 0).toString()} / ${
        this.props.refund.totalUnits
      }`;
      const adminFee = `Admin Fee: ${(this.props.refund.adminFee || 0).toFixed(2)}`;
      const serviceCharges = `Service Charges: ${(this.props.refund.serviceCharges || 0).toFixed(2)}`;
      const pastDueServiceFee = `Past Due Service Fee: ${(this.props.refund.pastDueServiceFee || 0).toFixed(2)}`;

      const remainingBalance = `Remaining Balance: ${+this.props.refund.totalUnits -
        +this.props.refund.unitsUsed +
        ' x $' +
        (this.props.refund.averageUnitRate || 0).toFixed(2) +
        ' = $' +
        (this.props.refund.remainingBalance || 0).toFixed(2)}`;

      const refundAmount = `Amount to Be Refunded: ${
        this.props.refund.refundAmount > 0
          ? '$' +
            (this.props.refund.remainingBalance || 0).toFixed(2) +
            ' - $' +
            (this.props.refund.adminFee || 0).toFixed(2) +
            ' - $' +
            (this.props.refund.serviceCharges || 0).toFixed(2) +
            ' - $' +
            (this.props.refund.pastDueServiceFee || 0).toFixed(2) +
            ' = $' +
            (this.props.refund.refundAmount || 0).toFixed(2)
          : 'No Refund'
      }`;
      const commentBody = [
        cancellationDate,
        amountPaid,
        totalPrice,
        avgRate,
        timeUsed,
        adminFee,
        serviceCharges,
        pastDueServiceFee,
        remainingBalance,
        refundAmount
      ].join('\n\n');
      this.props.cancelContractCommentStamp(this.contractId, { comment: commentBody }, true);
    }
  }

  isOverrideRequired(val: string): boolean {
    if (this.props.addressOverride) {
      return hasValue(val);
    }
    return true;
  }

  renderInvalidEmailError() {
    return this.state.invalidEmail ? (
      <div className="col-xs-12">
        <div className="error">
          <span>Invalid Email Address</span>
        </div>
      </div>
    ) : (
      ''
    );
  }

  renderButtons() {
    const today = new Date();
    const isFuture = this.cancellationDate && moment(today).diff(this.cancellationDate) < 0;
    return (
      <div className="col-md-12">
        <div className="row end-xs text-right">
          <Link
            className="btn btn-secondary"
            to={getRoute(RouteDefinitions.contract.Details, {
              id: this.contractId
            })}
          >
            Cancel
          </Link>
          <button className="btn btn-primary" type="submit" disabled={!!isFuture}>
            Submit
          </button>
        </div>
      </div>
    );
  }

  renderRightSide() {
    if (this.props.isTermination) {
      return this.renderTermination();
    } else if (this.props.isTermination === false) {
      return this.renderCancellation();
    }
    return <></>;
  }

  renderTermination() {
    return (
      <div className="col-md-6">
        <div className="form-control col-md-12 mb-10 pb-10">
          <DateTimePicker
            formString="cancelContractForm"
            modelString=".cancellationDate"
            label="CancellationDate"
            requiresValue={true}
            className="col-md-12 mb-10 pb-10"
          />
        </div>
        <Errors
          className="error"
          model=".cancelDate"
          show={{ touched: true, focus: false }}
          messages={{ ...getDefaultErrorMessages(), validDate: 'Invalid Date' }}
        />
      </div>
    );
  }

  renderCancellation() {
    if (this.props.refund) {
      return (
        <div className="col-lg-6 dark-gray-bc">
          <h3>Financial Info</h3>
          <div className="row">
            <div className="col-md-6">
              <div className="form-control col-md-6">
                <label>Amount Paid</label>
                <h5>{'$' + (this.props.refund.amountPaid || 0).toFixed(2)}</h5>
              </div>
              <div className="form-control col-md-6">
                <label>Total Price</label>
                <h5>{'$' + (this.props.refund.totalPrice || 0).toFixed(2)}</h5>
              </div>
              <div className="form-control col-md-12">
                <label>Avg. Rate of Contract per {this.props.refund.unitName}</label>
                <h5>{'$' + (this.props.refund.averageUnitRate || 0).toFixed(2)}</h5>
              </div>
              <div className="form-control col-md-12">
                <label>{this.props.refund.unitName} Used</label>
                <h5>{(this.props.refund.unitsUsed || 0).toString() + ' / ' + this.props.refund.totalUnits}</h5>
              </div>
              <div className="form-control col-md-12">
                <label>Remaining Balance</label>
                <h5>
                  {+this.props.refund.totalUnits -
                    +this.props.refund.unitsUsed +
                    ' x $' +
                    (this.props.refund.averageUnitRate || 0).toFixed(2) +
                    ' = $' +
                    (this.props.refund.remainingBalance || 0).toFixed(2)}
                </h5>
              </div>
            </div>
            <div className="col-md-6">
              <div className="form-control col-md-12 mb-10 pb-10">
                <DateTimePicker
                  formString="cancelContractForm"
                  modelString=".cancellationDate"
                  label="CancellationDate"
                  requiresValue={true}
                  minDate={this.props.termStart}
                  maxDate={this.props.termEnd}
                  className="col-md-12 mb-10 pb-10"
                />

                <Errors
                  className="error"
                  model="cancelContractForm.cancellationDate"
                  show={{ touched: true, focus: false }}
                  messages={{
                    ...getDefaultErrorMessages(),
                    validDate: 'Invalid Date'
                  }}
                />
              </div>
              <div className="form-control col-md-12 mb-10 pb-10">
                <label htmlFor="cancelContractForm.adminFee">Admin Fee</label>
                <Control.text model=".adminFee" />
              </div>
              <div className="form-control col-md-12 mb-10 pb-10">
                <label htmlFor="cancelContractForm.serviceCharges">Service Charges</label>
                <Control.text model=".serviceCharges" />
              </div>
              <div className="form-control col-md-12 mb-10 pb-10">
                <label htmlFor="cancelContractForm.pastDueServiceFee">Past Due Service Fee</label>
                <Control.text model=".pastDueServiceFee" />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="form-control col-md-12 mt-10 pt-10">
                <label>Amount to Be Refunded</label>
                <h2>
                  {this.props.refund.refundAmount > 0
                    ? '$' +
                      (this.props.refund.remainingBalance || 0).toFixed(2) +
                      ' - $' +
                      (this.props.refund.adminFee || 0).toFixed(2) +
                      ' - $' +
                      (this.props.refund.serviceCharges || 0).toFixed(2) +
                      ' - $' +
                      (this.props.refund.pastDueServiceFee || 0).toFixed(2) +
                      ' = $' +
                      (this.props.refund.refundAmount || 0).toFixed(2)
                    : 'No Refund'}
                </h2>
                <Control.text model=".refundAmount" className="txt-hidden" />
              </div>
            </div>
          </div>
        </div>
      );
    }
    return;
  }

  render() {
    return (
      <div className={[classes.contractCancellation, 'row'].join(' ')}>
        <div className="col-xs-12">
          {this.props.refund ? (
            <FormWrapper
              shouldFormReset={true}
              model="cancelContractForm"
              hideNativeErrors={true}
              onChange={this.handleChange}
              onSubmit={this.handleSubmit}
            >
              <h2>Cancel Contract</h2>
              <div className="row mb-10 pb-10">
                <div className="col-lg-6">
                  <div className="form-control col-md-12">
                    <label htmlFor="cancelContractForm.reason.id">Cancellation Reason</label>
                    <Control.select model=".reason.id" validators={{ required: hasValue }}>
                      <Options options={this.props.cancellationReasons} />
                    </Control.select>
                    <Errors
                      className="error"
                      model=".reason.id"
                      show={{ touched: true, focus: false }}
                      messages={getDefaultErrorMessages()}
                    />
                  </div>
                  <div className="form-control col-md-12 mb-10">
                    <label htmlFor="cancelContractForm.reasonNote">Reason Notes</label>
                    <Control.textarea model=".reasonNote" rows={10} validators={{ required: hasValue, hasAlpha }} />
                    <Errors
                      className="error"
                      model=".reasonNote"
                      show={{ touched: true, focus: false }}
                      messages={getDefaultErrorMessages()}
                    />
                  </div>
                </div>
                {this.renderRightSide()}
              </div>
              <div className="row">
                <div className="col-lg-6">
                  <h3>Refund Info</h3>
                  {this.props.paymentInfo &&
                  this.props.paymentInfo.paymentType &&
                  this.props.paymentInfo.paymentType.id !== PaymentType.None ? (
                    <>
                      <div className="col-md-12 mb-10">
                        <h4>
                          <strong>
                            Your contract was paid for with a
                            {' ' +
                              PaymentType[this.props.paymentInfo.paymentType.id] +
                              ' by ' +
                              this.props.paymentInfo.payerName +
                              ' '}
                            and will be refunded accordingly.
                          </strong>
                        </h4>
                      </div>
                      {this.props.paymentInfo.paymentType.id === PaymentType.CreditCard && (
                        <div className="row">
                          <div className="col-xs-12">
                            <CheckboxInline>
                              <Control.checkbox model=".refundByCheck" onChange={this.handleRefundByCheck} />
                              <label htmlFor="cancelContractForm.refundByCheck">Refund By Check</label>
                            </CheckboxInline>
                          </div>
                        </div>
                      )}
                      {(this.props.paymentInfo.paymentType.id === PaymentType.ACH ||
                        (this.props.paymentInfo.paymentType.id === PaymentType.CreditCard &&
                          this.props.refundByCheck)) && (
                        <div className="row">
                          <div className="col-xs-12">
                            <CheckboxInline>
                              <Control.checkbox model=".addressOverride" onChange={this.handleOverrideAddress} />
                              <label htmlFor="cancelContractForm.addressOverride">Override Mailing Address</label>
                            </CheckboxInline>
                          </div>
                          {this.props.addressOverride ? (
                            <>
                              <div className="col-md-12">
                                <h3>Mailing Address</h3>
                              </div>
                              <div className="form-control col-md-6">
                                <label htmlFor="cancelContractForm.refundAddress.line1">Address 1</label>
                                <Control.text
                                  model=".refundAddress.line1"
                                  validators={{
                                    required: this.isOverrideRequired,
                                    hasAlpha: hasAlpha,
                                    hasNumber
                                  }}
                                />
                                <Errors
                                  className="error"
                                  model=".refundAddress.line1"
                                  show={{ touched: true, focus: false }}
                                  messages={getDefaultErrorMessages()}
                                />
                              </div>
                              <div className="form-control col-md-6">
                                <label htmlFor="cancelContractForm.refundAddress.line2">Address 2 (optional)</label>
                                <Control.text model=".refundAddress.line2" />
                              </div>
                              <div className="form-control col-md-6">
                                <label htmlFor="cancelContractForm.refundAddress.city">City</label>
                                <Control.text
                                  model=".refundAddress.city"
                                  validators={{
                                    required: this.isOverrideRequired,
                                    isValidCity
                                  }}
                                />
                                <Errors
                                  className="error"
                                  model=".refundAddress.city"
                                  show={{ touched: true, focus: false }}
                                  messages={getDefaultErrorMessages()}
                                />
                              </div>
                              <div className="form-control col-md-4">
                                <label htmlFor="cancelContractForm.refundAddress.state.abbreviation">State</label>
                                <Select
                                  model=".refundAddress.state.abbreviation"
                                  identifier={DynamicSelectIdentifier.USStates}
                                  validators={{
                                    required: this.isOverrideRequired
                                  }}
                                />
                                <Errors
                                  className="error"
                                  model=".refundAddress.state.abbreviation"
                                  show={{ touched: true, focus: false }}
                                  messages={getDefaultErrorMessages()}
                                />
                              </div>
                              <div className="form-control col-md-2">
                                <label htmlFor="cancelContractForm.refundAddress.zip">Zip</label>
                                <Control.text
                                  model=".refundAddress.zip"
                                  validators={{
                                    required: this.isOverrideRequired,
                                    isValidZip: isValidZip
                                  }}
                                />
                                <Errors
                                  className="error"
                                  model=".refundAddress.zip"
                                  show={{ touched: true, focus: false }}
                                  messages={getDefaultErrorMessages()}
                                />
                              </div>
                            </>
                          ) : (
                            ''
                          )}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="col-md-12 mb-10">
                      <h4>
                        <strong>No payments have been made toward this contract's premium.</strong>
                      </h4>
                    </div>
                  )}
                </div>
                <div className="col-lg-6">
                  <h3>Notifications</h3>
                  <div className="row mb-10 pb-10">
                    <label className="mb-10">Please select the parties you wish to notify of the cancellation</label>
                    <div className="col-xs-12">
                      <div className="row">
                        {this.props.cancellationNotifications.map(notification => {
                          return (
                            <div className="col-md-4" key={notification.email}>
                              <CheckboxInline>
                                <Control.checkbox model=".emailNotifications[]" value={notification.email} />
                                <label htmlFor="cancelContractForm.notifications">{notification.description}</label>
                              </CheckboxInline>
                            </div>
                          );
                        })}
                      </div>
                      <div className="row bottom-xs mt-10 mb-10">
                        <div className="col-md-6">
                          <label>Additional Cancellation Notifications</label>
                          <input id="emailAddress" type="email" />
                        </div>
                        <button
                          className="btn btn-secondary"
                          type="button"
                          onClick={(event: any) => this.handleAddNotification(event)}
                        >
                          Add
                        </button>
                        {this.renderInvalidEmailError()}
                        <div className="col-xs-12">
                          <ul className="list-unstyled inline-block">
                            {this.props.emailNotifications.map((email: string) => {
                              return (
                                <li key={email} className="mt-5">
                                  <Chip value={email} handleRemove={this.handleRemoveNotification} />
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {this.renderButtons()}
            </FormWrapper>
          ) : (
            <SpinnerPage />
          )}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/contract/_components/contract-cancellation/contract-cancellation.component.tsx